import React, { useCallback, useEffect, useState } from "react";
import {
    Alert, Button, Descriptions, Divider, Drawer, Empty, Input, InputNumber, List, Popconfirm, Space, Spin, Tag, Typography,
    message,
} from "antd";
import { SendOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import { ENTREPRISE_ADMIN_URL as ENTREPRISE_ADMIN } from "../../../config/entreprisePage";
import { formatMoney } from "../../../utils/money";

const { Text, Paragraph } = Typography;

// NegotiationView.status, vu par l'acheteur
export const NEGOTIATION_STATUS = {
    NOT_STARTED: { label: "Pas encore commencée", color: "default" },
    OPEN: { label: "En cours", color: "blue" },
    FINAL_OFFER: { label: "Offre finale à examiner", color: "orange" },
    AGREED: { label: "Accord conclu", color: "green" },
    DECLINED: { label: "Offre finale refusée", color: "red" },
    CANCELLED: { label: "Terminée", color: "default" },
};


const formatDateTime = (value) => (value ? dayjs(value).format("DD/MM/YYYY HH:mm") : "");

const quantity = (value, unit) =>
    `${new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 2 }).format(Number(value))} ${unit || ""}`.trim();

const terms = (value) => (value
    ? `${quantity(value.quantity, value.measurementUnit)} à ${formatMoney(value.unitPrice, value.currency?.code)} / ${value.measurementUnit}`
    : "-");

const total = (value) => formatMoney(Number(value.quantity) * Number(value.unitPrice), value.currency?.code);

const backendMessage = (error, fallback) => error?.response?.data?.message || fallback;

const EMPTY_MESSAGE = { body: "", proposedUnitPrice: null, proposedQuantity: null };

/**
 * Négociation d'une demande envoyée, vue par l'acheteur : 3 messages par partie, éventuellement
 * avec un prix ou une quantité proposés, puis le vendeur fait son offre finale (nouveaux termes ou
 * offre de base maintenue) que l'acheteur accepte ou refuse. Refuser retire la demande.
 *
 * @param api         presaleNegotiationApi ou tradingNegotiationApi
 * @param country     pays du vendeur (base où vit la négociation)
 * @param seller      « le producteur » ou « le négociant »
 * @param agreedText  ce qui suit l'accord (contrat de prévente, achat conclu…)
 * @param sellerPath  écran de l'espace entreprise où le vendeur négocie (si l'utilisateur est le vendeur)
 */
const BuyerNegotiationDrawer = ({
    api, requestId, country, open, onClose, onChanged, title = "Négociation", seller = "le vendeur", agreedText, sellerPath,
}) => {
    const [state, setState] = useState({ status: "idle" });
    const [draft, setDraft] = useState(EMPTY_MESSAGE);
    const [busy, setBusy] = useState(null);

    const load = useCallback(async () => {
        if (!requestId) return;
        setState({ status: "loading" });
        try {
            setState({ status: "success", view: await api.thread(country, requestId) });
        } catch (error) {
            setState({ status: "error", error: backendMessage(error, "Impossible de charger la négociation.") });
        }
    }, [api, country, requestId]);

    useEffect(() => {
        if (open) {
            setDraft(EMPTY_MESSAGE);
            load();
        }
    }, [open, load]);

    const run = async (key, action, success) => {
        setBusy(key);
        try {
            setState({ status: "success", view: await action() });
            message.success(success);
            onChanged?.();
            return true;
        } catch (error) {
            message.error(backendMessage(error, "L'opération n'a pas pu être enregistrée."));
            if (error?.response?.status === 409) load();
            return false;
        } finally {
            setBusy(null);
        }
    };

    const send = async () => {
        if (await run("post", () => api.post(country, requestId, draft), "Message envoyé")) setDraft(EMPTY_MESSAGE);
    };

    const accept = () => run("accept", () => api.accept(country, requestId), "Offre finale acceptée ✅");
    const decline = () => run("decline", () => api.decline(country, requestId), "Offre finale refusée : la demande est retirée.");

    const view = state.view;

    const body = () => {
        if (state.status === "loading" || state.status === "idle") return <Spin />;
        if (state.status === "error") return <Alert type="error" showIcon title={state.error} />;
        if (view.side === "SELLER") {
            return (
                <Alert type="info" showIcon title="Vous êtes le vendeur de cette demande"
                       description={<>Répondez à l&apos;acheteur depuis votre <a href={`${ENTREPRISE_ADMIN}${sellerPath || ""}`}>espace entreprise</a>.</>} />
            );
        }
        const status = NEGOTIATION_STATUS[view.status] || { label: view.status, color: "default" };
        const unit = view.base?.measurementUnit;
        const currency = view.base?.currency?.code;
        return (
            <>
                <Descriptions size="small" column={1} bordered>
                    <Descriptions.Item label="Statut"><Tag color={status.color}>{status.label}</Tag></Descriptions.Item>
                    <Descriptions.Item label="Votre demande">{terms(view.base)}</Descriptions.Item>
                    {view.finalOffer && (
                        <Descriptions.Item label="Offre finale">
                            {terms(view.finalOffer)}, soit {total(view.finalOffer)}
                            {view.finalOfferAt && <Text type="secondary"> · le {formatDateTime(view.finalOfferAt)}</Text>}
                        </Descriptions.Item>
                    )}
                    <Descriptions.Item label="Messages restants">
                        Vous : {view.myMessagesLeft} / {view.maxMessagesPerSide} · Vendeur : {view.otherMessagesLeft} / {view.maxMessagesPerSide}
                    </Descriptions.Item>
                </Descriptions>

                {view.canRespond && (
                    <Alert style={{ marginTop: 12 }} type="warning" showIcon
                           title={`${seller.charAt(0).toUpperCase()}${seller.slice(1)} vous a fait son offre finale`}
                           description={
                               <Space orientation="vertical">
                                   <Text>Acceptez-la pour conclure, ou refusez-la : votre demande sera alors retirée.</Text>
                                   <Space wrap>
                                       <Popconfirm title="Accepter l'offre finale ?" okText="Accepter" cancelText="Annuler"
                                                   onConfirm={accept}>
                                           <Button type="primary" loading={busy === "accept"}>Accepter l&apos;offre finale</Button>
                                       </Popconfirm>
                                       <Popconfirm title="Refuser l'offre finale ? Votre demande sera retirée." okText="Refuser"
                                                   cancelText="Annuler" onConfirm={decline}>
                                           <Button danger loading={busy === "decline"}>Refuser</Button>
                                       </Popconfirm>
                                   </Space>
                               </Space>
                           } />
                )}
                {view.status === "AGREED" && (
                    <Alert style={{ marginTop: 12 }} type="success" showIcon
                           title={`Accord conclu${agreedText ? ` : ${agreedText}` : "."}`} />
                )}

                <Divider titlePlacement="start">Échanges</Divider>
                <List
                    dataSource={view.messages || []}
                    locale={{ emptyText: <Empty description="Aucun message pour l'instant" /> }}
                    renderItem={(item) => (
                        <List.Item style={{ justifyContent: item.mine ? "flex-end" : "flex-start" }}>
                            <div style={{
                                maxWidth: "85%", padding: "8px 12px", borderRadius: 8,
                                background: item.mine ? "#e8f5e9" : "#f5f5f5",
                            }}>
                                <Text strong>{item.mine ? "Vous" : item.authorName || "Vendeur"}</Text>
                                <Text type="secondary"> · {formatDateTime(item.createdAt)}</Text>
                                <Paragraph style={{ margin: "4px 0 0", whiteSpace: "pre-wrap" }}>{item.body}</Paragraph>
                                {(item.proposedUnitPrice || item.proposedQuantity) && (
                                    <Space wrap size="small">
                                        {item.proposedUnitPrice && (
                                            <Tag>Prix proposé : {formatMoney(item.proposedUnitPrice, currency)} / {unit}</Tag>
                                        )}
                                        {item.proposedQuantity && <Tag>Quantité proposée : {quantity(item.proposedQuantity, unit)}</Tag>}
                                    </Space>
                                )}
                            </div>
                        </List.Item>
                    )}
                />

                {view.canPost && (
                    <Space orientation="vertical" style={{ width: "100%", marginTop: 8 }}>
                        <Input.TextArea rows={3} maxLength={2000} value={draft.body} aria-label="Votre message"
                                        placeholder={`Votre message à ${seller}`}
                                        onChange={(e) => setDraft({ ...draft, body: e.target.value })} />
                        <Space wrap>
                            <InputNumber min={0} value={draft.proposedUnitPrice} aria-label="Prix proposé"
                                         placeholder="Prix proposé (facultatif)" style={{ width: 200 }}
                                         suffix={`${currency || ""} / ${unit || ""}`}
                                         onChange={(value) => setDraft({ ...draft, proposedUnitPrice: value })} />
                            <InputNumber min={0} value={draft.proposedQuantity} aria-label="Quantité proposée"
                                         placeholder="Quantité (facultatif)" style={{ width: 180 }} suffix={unit}
                                         onChange={(value) => setDraft({ ...draft, proposedQuantity: value })} />
                            <Button type="primary" icon={<SendOutlined />} loading={busy === "post"}
                                    disabled={!draft.body.trim()} onClick={send}>
                                Envoyer
                            </Button>
                        </Space>
                    </Space>
                )}
                {!view.canPost && ["NOT_STARTED", "OPEN"].includes(view.status) && view.myMessagesLeft === 0 && (
                    <Paragraph type="secondary" style={{ marginTop: 8 }}>
                        Vous avez envoyé vos {view.maxMessagesPerSide} messages : {seller} va vous faire son offre finale.
                    </Paragraph>
                )}
            </>
        );
    };

    return (
        <Drawer title={title} open={open} onClose={onClose} size={560} destroyOnHidden>
            {body()}
        </Drawer>
    );
};

export default BuyerNegotiationDrawer;
